import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { motion } from 'motion/react';
import { Mail, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { Logo } from '../components/Logo';
import { OTPInput } from '../components/OTPInput';
import { CountdownTimer } from '../components/CountdownTimer';
import { SuccessAnimation } from '../components/SuccessAnimation';
import { projectId, publicAnonKey } from '/utils/supabase/info';

export function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  
  console.log('🔍 VerifyEmail component rendering...');
  console.log('📍 Location object:', location);
  console.log('📦 Location state:', location.state);
  console.log('📧 Email from state:', location.state?.email);
  console.log('🔑 Code from state:', location.state?.verificationCode);
  
  // Try to get email from location state or localStorage (backup)
  const emailFromState = location.state?.email || '';
  const emailFromStorage = typeof window !== 'undefined' ? localStorage.getItem('pendingVerificationEmail') : '';
  const email = emailFromState || emailFromStorage || '';
  
  const codeFromSignup = location.state?.verificationCode || '';
  
  console.log('✅ Final email value:', email);
  console.log('✅ Final code value:', codeFromSignup);
  
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [newCode, setNewCode] = useState(codeFromSignup);
  const [codeExpiry, setCodeExpiry] = useState<string>(
    new Date(Date.now() + 15 * 60 * 1000).toISOString()
  );

  useEffect(() => {
    console.log('📧 VerifyEmail useEffect - Checking email availability');
    console.log('Email value in useEffect:', email);
    
    // Skip redirect if verification was successful
    if (success) {
      console.log('✅ Verification successful, skipping email check');
      return;
    }
    
    // Save email to localStorage as backup
    if (emailFromState && typeof window !== 'undefined') {
      console.log('💾 Saving email to localStorage:', emailFromState);
      localStorage.setItem('pendingVerificationEmail', emailFromState);
    }
    
    // If no email in state or storage, redirect to signup after a brief delay
    if (!email) {
      console.log('ℹ️ No email found - redirecting to signup');
      const timeout = setTimeout(() => {
        navigate('/signup', { replace: true });
      }, 100);
      return () => clearTimeout(timeout);
    } else {
      console.log('✅ Email found, staying on verification page');
    }
  }, [email, emailFromState, navigate, success]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Clear localStorage when verification is successful
  const clearPendingEmail = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('pendingVerificationEmail');
    }
  };

  // Don't render anything if no email (will redirect)
  if (!email) {
    console.log('🚫 No email - returning null');
    return null;
  }
  
  console.log('✅ Rendering VerifyEmail component with email:', email);

  // Mask email for privacy (show only first and last char + domain)
  const maskEmail = (email: string) => {
    const [local, domain] = email.split('@');
    if (local.length <= 2) return email;
    const masked = local[0] + '***' + local[local.length - 1];
    return `${masked}@${domain}`;
  };

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const newCode = [...verificationCode];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }
    
    setVerificationCode(newCode);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);

    try {
      console.log('[VERIFY] Submitting verification code:', code);
      console.log('[VERIFY] Email:', email);
      
      const url = `https://${projectId}.supabase.co/functions/v1/make-server-355b1994/auth/verify-email`;
      console.log('[VERIFY] API URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({ email, code }),
      });

      console.log('[VERIFY] Response status:', response.status);
      console.log('[VERIFY] Response headers:', Object.fromEntries(response.headers.entries()));
      
      // Try to parse response as JSON
      let data;
      const contentType = response.headers.get('content-type');
      console.log('[VERIFY] Content-Type:', contentType);
      
      // Get response text first for better error handling
      const responseText = await response.text();
      console.log('[VERIFY] Raw response:', responseText);
      
      try {
        data = JSON.parse(responseText);
        console.log('[VERIFY] Parsed JSON data:', data);
      } catch (parseError) {
        console.error('[VERIFY] ❌ Failed to parse JSON:', parseError);
        console.error('[VERIFY] ❌ Response text:', responseText.substring(0, 500));
        throw new Error(`Server returned invalid response: ${responseText.substring(0, 100)}`);
      }

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      // Success - navigate to login
      console.log('[VERIFY] ✅ Verification successful');
      navigate('/login', { 
        state: { 
          message: 'Email verified successfully! Please sign in.',
          email 
        } 
      });
      clearPendingEmail();
      setSuccess(true);
    } catch (err: any) {
      console.error('[VERIFY] ❌ Error:', err);
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setResendSuccess(false);
    setError('');

    try {
      const url = `https://${projectId}.supabase.co/functions/v1/make-server-355b1994/auth/resend-verification`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend code');
      }

      // Update the displayed code
      if (data.verificationCode) {
        setNewCode(data.verificationCode);
      }

      // Reset code expiry
      setCodeExpiry(new Date(Date.now() + 15 * 60 * 1000).toISOString());

      setResendSuccess(true);
      setResendCountdown(60); // 60 second countdown
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleOTPComplete = async (code: string) => {
    setError('');
    setLoading(true);

    try {
      console.log('[VERIFY] Auto-submitting code:', code);
      
      const url = `https://${projectId}.supabase.co/functions/v1/make-server-355b1994/auth/verify-email`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({ email, code }),
      });

      const responseText = await response.text();
      const data = JSON.parse(responseText);

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      // Success!
      console.log('[VERIFY] ✅ Verification successful');
      setSuccess(true);
      clearPendingEmail();
      
      // Navigate to login after a short delay to show success animation
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Email verified successfully! Please sign in.',
            email 
          } 
        });
      }, 2000);
    } catch (err: any) {
      console.error('[VERIFY] ❌ Error:', err);
      setError(err.message || 'Verification failed. Please try again.');
      setLoading(false);
    }
  };

  // If success, show success animation then redirect
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
        <SuccessAnimation 
          message="Email Verified!"
          onComplete={() => {
            navigate('/login', { 
              state: { 
                message: 'Email verified successfully! Please sign in.',
                email 
              } 
            });
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="mb-4"
          >
            <div className="w-48 mx-auto">
              <Logo className="w-full h-auto" />
            </div>
          </motion.div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Verify Your Email</h1>
          <p className="text-slate-600">
            We've sent a 6-digit code to<br />
            <span className="font-semibold text-slate-900">{maskEmail(email)}</span>
          </p>
          
          {/* Development Mode: Show verification code if available */}
          {newCode && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg"
            >
              <p className="text-sm text-amber-800 mb-1 font-medium">
                📧 Email Delivery Note
              </p>
              <p className="text-xs text-amber-700 mb-2">
                Your verification code: <span className="font-mono font-bold text-lg tracking-wider">{newCode}</span>
              </p>
              <p className="text-xs text-amber-600">
                Note: Email sending may be limited. In production, this will be sent to your inbox.
              </p>
            </motion.div>
          )}
        </div>

        {/* Verification Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border-2 border-slate-200 shadow-xl p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-red-50 border-2 border-red-200 rounded-lg flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </motion.div>
            )}

            {/* Success Message */}
            {resendSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-green-50 border-2 border-green-200 rounded-lg flex items-start gap-3"
              >
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-700">Verification code sent successfully!</p>
              </motion.div>
            )}

            {/* Verification Code Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-4 text-center">
                Enter Verification Code
              </label>
              
              {/* OTP Input Component */}
              <OTPInput 
                length={6}
                onComplete={handleOTPComplete}
                error={!!error}
                disabled={loading}
              />
              
              {/* Countdown Timer */}
              <div className="mt-4">
                <CountdownTimer 
                  expiresAt={codeExpiry}
                  onExpire={() => setError('Code has expired. Please request a new one.')}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || verificationCode.join('').length !== 6}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white font-semibold rounded-xl shadow-lg shadow-[#4F819F]/30 hover:shadow-xl hover:shadow-[#4F819F]/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Verify Email
                </>
              )}
            </button>

            {/* Resend Code */}
            <div className="text-center">
              <p className="text-sm text-slate-600 mb-2">
                Didn't receive the code?
              </p>
              {resendCountdown > 0 ? (
                <p className="text-sm text-slate-500">
                  Resend available in <span className="font-semibold text-[#4F819F]">{resendCountdown}s</span>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resending}
                  className="text-[#4F819F] font-semibold hover:underline disabled:opacity-50 flex items-center gap-1 mx-auto"
                >
                  {resending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Resend Code
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </motion.div>

        {/* Info Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl"
        >
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-semibold mb-1">Check your email</p>
              <p>The verification code will expire in 15 minutes. Make sure to check your spam folder if you don't see the email.</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}